import { motion } from "framer-motion";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import { Mail, MessageCircle, Bug, Lightbulb, BookOpen, Zap } from "lucide-react";
import foxMascot from "@/assets/fox-scenario-creator.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const HELP_CARDS = [
  {
    icon: Zap,
    title: "Jak zacząć?",
    description:
      "Zarejestruj się, podaj swój nick na Instagramie, dodaj źródła inspiracji i wygeneruj pierwszy scenariusz. Cały proces zajmuje kilka minut.",
  },
  {
    icon: BookOpen,
    title: "Źródła inspiracji",
    description:
      "Dodaj do 5 kont z Twojej branży. Nasza AI przeanalizuje ich najlepsze posty i na tej podstawie stworzy scenariusze dopasowane do Ciebie.",
  },
  {
    icon: Lightbulb,
    title: "Generowanie scenariuszy",
    description:
      "Wybierz temat, tryb generowania (podstawowy lub rozszerzony) i poczekaj kilka minut. Gotowy scenariusz znajdziesz w bibliotece.",
  },
  {
    icon: Bug,
    title: "Zgłaszanie problemów",
    description:
      "Kliknij ikonę dymku w prawym dolnym rogu aplikacji, aby zgłosić błąd lub zaproponować ulepszenie. Każde zgłoszenie jest analizowane.",
  },
];

const CONTACT_OPTIONS = [
  {
    icon: Mail,
    label: "Email",
    value: "kontakt@viraluj.pl",
    href: "mailto:kontakt@viraluj.pl",
  },
  {
    icon: MessageCircle,
    label: "Instagram DM",
    value: "@viraluj.pl",
    href: "https://instagram.com/viraluj.pl",
  },
];

const Pomoc = () => {
  return (
    <div className="min-h-screen text-gray-900">
      <Header />

      <section className="relative min-h-screen pt-28 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9B896] via-[#FABFA3] to-[#FCCAB0]" />

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          {/* Hero */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="text-center mb-14"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4">
              Centrum pomocy
            </h1>
            <p className="text-lg text-gray-700 max-w-md mx-auto">
              Dowiedz się, jak w pełni wykorzystać Viraluj.pl
            </p>
          </motion.div>

          {/* Help cards */}
          <div className="grid sm:grid-cols-2 gap-6 mb-14">
            {HELP_CARDS.map((card, i) => (
              <motion.div
                key={card.title}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={i + 1}
                className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-6 space-y-3"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <card.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{card.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact strip */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={5}
            className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-6 md:p-8"
          >
            <h2 className="font-semibold text-xl mb-1 text-center">Potrzebujesz więcej pomocy?</h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              Napisz do nas — odpowiadamy w ciągu 24 godzin
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              {CONTACT_OPTIONS.map((opt) => (
                <a
                  key={opt.label}
                  href={opt.href}
                  target={opt.href.startsWith("http") ? "_blank" : undefined}
                  rel={opt.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-3 text-sm font-medium text-primary hover:underline"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <opt.icon className="w-4 h-4 text-primary" />
                  </div>
                  {opt.value}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Fox */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={6}
            className="flex justify-center mt-12"
          >
            <img
              src={foxMascot}
              alt="Viraluj Fox"
              className="w-36 md:w-44 object-contain"
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pomoc;
